import { useEffect, useRef, useState } from "react";
import { BiChevronDown } from "react-icons/bi";
import { FiChevronsDown, FiLogOut, FiUser, FiX } from "react-icons/fi";
import { HiOutlineMenuAlt3 } from "react-icons/hi";
import { MdOutlineRequestQuote } from "react-icons/md";
import {
  RiBuilding4Line,
  RiDashboardLine,
  RiUserSearchLine,
} from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import logo from "../assets-user/logo.png";
import post_property_blue_icon from "../assets-user/navbar/post_property_blue_icon.png";
import post_property_icon from "../assets-user/property.svg";
import { useAuth } from "../context/AuthContext";
import { useFlowManager } from "../hooks/useFlowManager";
import { Z_INDEX } from "../utils/constants";

const Navbar = ({ handleHamburgerClick }) => {
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { handlePostPropertyClick } = useFlowManager();
  const closeTimer = useRef(null);
  const dropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const sidebarRef = useRef(null);

  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const handleMouseEnter = (menu) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(menu);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 200);
  };

  const handleSignIn = () => {
    navigate("/login");
    setOpenMenu(null);
  };

  const handleItemClick = (item) => {
    navigate(item?.path);
    setOpenMenu(null);
  };

  const onPostProperty = () => {
    handlePostPropertyClick(Boolean(user));
  };

  const handleSignOut = async () => {
    logout();
  };

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
    if (handleHamburgerClick) handleHamburgerClick();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('[data-hamburger="true"]')) return;

      if (sidebarRef.current && sidebarRef.current.contains(event.target))
        return;

      if (
        userDropdownRef.current &&
        userDropdownRef.current.contains(event.target)
      )
        return;

      if (event.target.closest('[data-user-menu="true"]')) return;

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenMenu(null);
      }

      if (sidebarOpen) setSidebarOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [sidebarOpen]);

  const sidebarMenuItems = [
    {
      icon: FiUser,
      label: "My Profile",
      path: "/admin/profile-subscription",
    },
    {
      icon: RiDashboardLine,
      label: "Dashboard",
      path: "/admin",
    },
    {
      icon: MdOutlineRequestQuote,
      label: "Requested Properties",
      path: "/requested-properties",
    },
    {
      icon: RiBuilding4Line,
      label: "My Properties",
      path: "/admin/properties",
    },
    {
      icon: RiUserSearchLine,
      label: "Lead / Response",
      path: "/admin/leads",
    },
  ];

  const menuConfig = [
    {
      title: "Buy",
      path: "/properties",
      dropdown: [
        { title: "Residential Apartments", path: "/properties" },
        { title: "Independent Houses", path: "/properties" },
        { title: "Plots / Land", path: "/properties" },
        { title: "Commercial Spaces", path: "/properties" },
      ],
    },
    {
      title: "Sell",
      path: "/admin/properties",
      dropdown: [
        { title: "Sell Residential Property", path: "/admin/properties" },
        { title: "Sell Commercial Property", path: "/admin/properties" },
        { title: "Sell Land / Plots", path: "/admin/properties" },
        { title: "Premium Listing", path: "/admin/properties" },
      ],
    },
    {
      title: "Interior",
      path: "/interior",
    },
  ];

  return (
    <>
      <header
        style={{ zIndex: Z_INDEX.navbar }}
        className="flex items-center justify-between bg-[#0C3861] px-3 sm:px-4 md:px-6 lg:px-10 py-1.5 h-16 sm:h-18 md:h-20 shadow-md fixed w-full"
      >
        {/* Left: Logo */}
        <div
          onClick={() => navigate(`/`)}
          className="flex items-center select-none cursor-pointer space-x-1.5 sm:space-x-2 h-full"
        >
          <img
            src={logo}
            alt="Logo"
            className="h-10 sm:h-12 md:h-full object-contain cursor-pointer"
          />
          <div className="flex flex-col cursor-default items-center leading-[0.9]">
            <span className="text-white font-audiowide font-medium text-lg sm:text-xl md:text-3xl tracking-wide">
              LeadBook
            </span>
            <div
              className="hidden md:flex font-poppins text-[#999999] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking- justify-between w-full"
              style={{
                marginTop: "-6px",
                letterSpacing: "0.3em ",
                textAlign: "justify",
              }}
            >
              <span className="w-full text-center">A Path To Success</span>
            </div>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 lg:space-x-4">
          {/* Desktop Menu */}
          <div
            ref={dropdownRef}
            className="hidden lg:flex space-x-4 xl:space-x-6 text-white relative"
          >
            {menuConfig.map((item) => (
              <div
                key={item.title}
                style={{ zIndex: Z_INDEX.dropdown }}
                className="relative"
                onMouseEnter={() =>
                  item.dropdown && handleMouseEnter(item.title)
                }
                onMouseLeave={item.dropdown ? handleMouseLeave : undefined}
              >
                <button
                  onClick={() => {
                    if (!item.dropdown) {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                      navigate(item.path);
                    } else toggleMenu(item.title);
                  }}
                  className={`flex items-center gap-1 text-sm xl:text-base transition ${
                    openMenu === item.title
                      ? "text-gray-300"
                      : "hover:text-gray-300"
                  }`}
                >
                  {item.title}
                  {item.dropdown && (
                    <BiChevronDown
                      className={`text-lg transition-transform duration-200 ${
                        openMenu === item.title ? "rotate-180 opacity-60" : ""
                      }`}
                    />
                  )}
                </button>

                {/* Desktop Dropdown */}
                {item.dropdown && (
                  <div
                    className={`absolute left-0 mt-2 w-56 bg-white text-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all duration-200 origin-top ${
                      openMenu === item.title
                        ? "scale-100 opacity-100 visible"
                        : "scale-95 opacity-0 invisible"
                    }`}
                  >
                    {item.dropdown.map((sub) => (
                      <div
                        key={sub.title}
                        onClick={() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                          navigate(sub.path);
                        }}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition text-sm"
                      >
                        {sub.title}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Interior Direct Button (Desktop Only) */}
          {/* <button
            onClick={() => {
              window.location.href = "/interior";
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className={`hidden lg:flex items-center gap-1 text-sm xl:text-base transition text-gray-300 hover:text-gray-300}`}
          >
            Interior
          </button> */}

          {/* Post Property (Desktop) */}
          <button
            onClick={onPostProperty}
            className="hidden lg:flex items-center justify-center gap-1.5 md:gap-2 bg-btn-brandRed hover:bg-btn-brandRedHover
              px-3 md:px-4 lg:px-5 py-1.5 sm:py-2 md:py-2 text-white font-normal text-sm md:text-base shadow-md hover:shadow-lg
              transform-gpu transition-all duration-100 ease-out rounded-full
              active:scale-[0.97] active:shadow-sm outline-none focus-visible:ring-2
              select-none cursor-pointer"
          >
            <span className="w-[1.38rem] h-[1.38rem] md:w-5 md:h-5 lg:w-6 lg:h-6 flex flex-shrink-0 select-none items-center justify-center">
              <img
                src={post_property_icon}
                alt="post-property"
                style={{ zIndex: Z_INDEX.base }}
                className="w-full h-full object-contain pointer-events-none select-none transform-gpu"
                draggable="false"
              />
            </span>
            <span className="hidden lg:inline transform-gpu">
              Post Property
            </span>
          </button>

          {/* User Avatar + Hamburger */}
          <div className="relative">
            <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 cursor-pointer">
              <div
                data-user-menu="true"
                onClick={() => toggleMenu("user")}
                className="hidden sm:flex items-center gap-2"
              >
                <button className="w-8 h-8 mx-1.5 sm:mx-0 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-sm sm:text-base">
                  {user?.fullName?.[0]?.toUpperCase() || "G"}
                </button>
                <FiChevronsDown
                  className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
                    openMenu === "user" ? "rotate-180" : ""
                  }`}
                />
              </div>

              {/* Avatar (Mobile) */}
              <button
                data-user-menu="true"
                onClick={() => toggleMenu("user")}
                className="sm:hidden w-8 h-8 mx-1.5 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-sm"
              >
                {user?.fullName?.[0]?.toUpperCase() || "G"}
              </button>

              <div
                className="lg:hidden"
                onClick={handleSidebarToggle}
                data-hamburger="true"
              >
                <HiOutlineMenuAlt3
                  strokeWidth={2.5}
                  className="text-white text-2xl sm:text-xl md:text-2xl lg:text-xl cursor-pointer w-8 h-8"
                  data-hamburger="true"
                />
              </div>
            </div>

            {/* User Dropdown */}
            {openMenu === "user" && (
              <div
                ref={userDropdownRef}
                style={{ pointerEvents: "auto", zIndex: Z_INDEX.sidebar }}
                className={`absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200
                  py-3 transform origin-top-right transition-all duration-200 ease-out
                  ${
                    openMenu === "user"
                      ? "scale-100 opacity-100"
                      : "scale-95 opacity-0"
                  }`}
              >
                {/* User Info */}
                <div className="px-5 pb-3 border-b border-gray-200 flex items-center space-x-3">
                  <button className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-base shadow-sm">
                    {user?.fullName?.[0]?.toUpperCase() || "G"}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {user?.fullName || "Guest User"}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email || "Not logged in"}
                    </p>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1.5">
                  {sidebarMenuItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleItemClick(item)}
                      className="w-full cursor-pointer flex items-center justify-between px-5 py-2.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-200 group"
                    >
                      <div className="flex items-center space-x-3">
                        <item.icon className="h-4.5 w-4.5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
                        <span className="font-medium text-sm">
                          {item.label}
                        </span>
                      </div>
                      {item.badge && (
                        <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full font-semibold shadow-sm">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                <div className="border-t border-gray-200 my-1"></div>

                {/* Sign Out / Sign In */}
                {user ? (
                  <button
                    onClick={handleSignOut}
                    className="flex items-center space-x-3 px-5 py-2.5 text-gray-700 hover:text-red-600 hover:bg-red-50/80 transition-all duration-200 group w-full"
                  >
                    <FiLogOut className="h-4.5 w-4.5 text-gray-500 group-hover:text-red-600 transition-colors" />
                    <span className="font-medium text-sm">Sign Out</span>
                  </button>
                ) : (
                  <button
                    onClick={handleSignIn}
                    className="flex items-center space-x-3 px-5 py-2.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-200 group w-full"
                  >
                    <FiUser className="h-4.5 w-4.5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
                    <span className="font-medium text-sm">Sign In</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
          style={{ zIndex: Z_INDEX.navbar + 1 }}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed top-0 right-0 h-full w-60 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out  lg:hidden ${
          sidebarOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ zIndex: Z_INDEX.navbar + 2 }}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between px-4 md:px-6 lg:px-10 py-1.5 h-16 sm:h-18 md:h-20  border-b border-gray-200">
          <h2 className="font-semibold text-gray-800">Menu</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <FiX className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Sidebar Content */}
        <div className=" flex flex-col h-full p-3 space-y-8">
          <div className=" space-y-1 overflow-y-auto ">
            {menuConfig.map((item, index) => {
              const isDropdownOpen = openMenu === item.title;

              return (
                <div key={index} className="space-y-2">
                  <button
                    onClick={() => {
                      if (item.dropdown) {
                        setOpenMenu(isDropdownOpen ? null : item.title);
                      } else {
                        setSidebarOpen(false);
                        navigate(item.path);
                      }
                    }}
                    className="w-full flex px-4 items-center justify-between  py-2 text-gray-800 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                  >
                    <span>{item.title}</span>
                    {item.dropdown && (
                      <BiChevronDown
                        className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
                          isDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </button>

                  {/* Sidebar Dropdown */}
                  {item.dropdown && isDropdownOpen && (
                    <div className="pl-2">
                      {item.dropdown.map((sub, subIndex) => (
                        <button
                          key={subIndex}
                          onClick={() => {
                            setSidebarOpen(false);
                            navigate(sub.path);
                          }}
                          className="w-full text-left px-4 py-2  text-[0.8em]  text-gray-600  hover:bg-gray-50 rounded-lg  transition-colors "
                        >
                          {sub.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sidebar Post Property Button */}
          <button
            onClick={() => {
              navigate("/post-property");
              setSidebarOpen(false);
            }}
            className="
                w-full mx-auto flex  items-center justify-center box-border gap-2 
                px-3 py-3
                text-[#0C3861] font-medium
                rounded-lg
                bg-white
                ring-1 ring-[#0C3861]
                hover:bg-[rgba(12,56,97,0.08)]
                active:bg-[rgba(12,56,97,0.12)]
                transition-all duration-200
                shadow-sm
                hover:shadow-md
                "
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <img
                src={post_property_blue_icon}
                alt="post-property"
                className="w-full h-full object-contain"
              />
            </span>
            <span>Post Property</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Navbar;

// import { useNavigate } from "react-router-dom";
// import { useEffect, useRef, useState } from "react";
// import logo from "../assets-user/logo.png";
// import post_property_icon from "../assets-user/property.svg";
// import post_property_blue_icon from "../assets-user/navbar/post_property_blue_icon.png";
// import { BiChevronDown } from "react-icons/bi";
// import { HiOutlineMenuAlt3 } from "react-icons/hi";
// import { FiChevronsDown, FiLogOut, FiUser, FiX } from "react-icons/fi";
// import { MdOutlineRequestQuote } from "react-icons/md";
// import {
//   RiBuilding4Line,
//   RiDashboardLine,
//   RiUserSearchLine,
// } from "react-icons/ri";
// import { Z_INDEX } from "../utils/constants";
// import { useAuth } from "../context/AuthContext";

// const Navbar = ({ handleHamburgerClick }) => {
//   const navigate = useNavigate();
//   const [openMenu, setOpenMenu] = useState(null);
//   const [sidebarOpen, setSidebarOpen] = useState(false);
//   const { user, logout } = useAuth();
//   const closeTimer = useRef(null);
//   const dropdownRef = useRef(null);
//   const userDropdownRef = useRef(null);
//   const sidebarRef = useRef(null);

//   const toggleMenu = (menu) => {
//     setOpenMenu(openMenu === menu ? null : menu);
//   };

//   const handleMouseEnter = (menu) => {
//     if (closeTimer.current) clearTimeout(closeTimer.current);
//     setOpenMenu(menu);
//   };

//   const handleMouseLeave = () => {
//     closeTimer.current = setTimeout(() => setOpenMenu(null), 200);
//   };

//   const handleSignIn = () => {
//     navigate("/login");
//     setOpenMenu(null);
//   };

//   const handleItemClick = (item) => {
//     navigate(item?.path);
//     setOpenMenu(null);
//   };

//   const handleSignOut = async () => {
//     logout();
//   };

//   const handleSidebarToggle = () => {
//     setSidebarOpen(!sidebarOpen);
//     if (handleHamburgerClick) handleHamburgerClick();
//   };

//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       // Check if click is on hamburger menu
//       if (event.target.closest('[data-hamburger="true"]')) {
//         return;
//       }

//       // Check if click is inside the sidebar
//       if (sidebarRef.current && sidebarRef.current.contains(event.target)) {
//         return;
//       }

//       // Check if click is inside the user dropdown
//       if (
//         userDropdownRef.current &&
//         userDropdownRef.current.contains(event.target)
//       ) {
//         return;
//       }

//       // Check if click is on user avatar/dropdown trigger
//       if (event.target.closest('[data-user-menu="true"]')) {
//         return;
//       }

//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
//         setOpenMenu(null);
//       }

//       // Close sidebar when clicking outside
//       if (sidebarOpen) {
//         setSidebarOpen(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, [sidebarOpen]);

//   const sidebarMenuItems = [
//     {
//       icon: FiUser,
//       label: "My Profile",
//       path: "/admin/profile",
//     },
//     {
//       icon: RiDashboardLine,
//       label: "Dashboard",
//       path: "/admin",
//     },
//     {
//       icon: MdOutlineRequestQuote,
//       label: "Requested Properties",
//       path: "/admin/requests",
//     },
//     {
//       icon: RiBuilding4Line,
//       label: "My Properties",
//       path: "/admin/properties",
//     },
//     {
//       icon: RiUserSearchLine,
//       label: "Lead / Response",
//       path: "/admin/leads",
//     },
//   ];

//   const handlePostProperty = () => {};

//   const hamburgerMenuItem = [
//     {
//       title: "Buy",
//       dropdown: [
//         "Residential Apartments",
//         "Independent Houses",
//         "Plots / Land",
//         "Commercial Spaces",
//       ],
//     },
//     {
//       title: "Sell",
//       dropdown: [
//         "Sell Residential Property",
//         "Sell Commercial Property",
//         "Sell Land / Plots",
//         "Premium Listing",
//       ],
//     },
//     {
//       title: "Interior",
//     },
//   ];

//   return (
//     <>
//       <header
//         style={{ zIndex: Z_INDEX.navbar }}
//         className="flex items-center justify-between bg-[#0C3861] px-3 sm:px-4 md:px-6 lg:px-10 py-1.5 h-16 sm:h-18 md:h-20 shadow-md fixed w-full"
//       >
//         {/* Left: Logo & Title */}
//         <div
//           onClick={() => navigate(`/`)}
//           className="flex items-center select-none cursor-pointer space-x-1.5 sm:space-x-2 h-full"
//         >
//           <img
//             src={logo}
//             alt="Logo"
//             className="h-10 sm:h-12 md:h-full object-contain cursor-pointer"
//           />
//           <div className="flex flex-col cursor-default items-center leading-[0.9]">
//             <span className="text-white font-audiowide font-medium text-lg sm:text-xl md:text-3xl tracking-wide">
//               LeadBook
//             </span>
//             <div
//               className="hidden md:flex font-poppins text-[#999999] text-[10px] sm:text-xs tracking-[0.3em] sm:tracking- justify-between w-full"
//               style={{
//                 marginTop: "-6px",
//                 letterSpacing: "0.3em ",
//                 textAlign: "justify",
//               }}
//             >
//               <span className="w-full text-center">A Path To Success</span>
//             </div>
//           </div>
//         </div>

//         {/* Right: Actions */}
//         <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-3 lg:space-x-4">
//           {/* Center: Navigation Links - Hidden on mobile */}
//           <div
//             ref={dropdownRef}
//             className="hidden lg:flex space-x-4 xl:space-x-6 text-white relative"
//           >
//             {/* BUY DROPDOWN */}
//             <div
//               style={{ zIndex: Z_INDEX.dropdown }}
//               className="relative"
//               onMouseEnter={() => handleMouseEnter("buy")}
//               onMouseLeave={handleMouseLeave}
//             >
//               <button
//                 onClick={() => toggleMenu("buy")}
//                 className={`flex items-center gap-1 text-sm xl:text-base transition ${
//                   openMenu === "buy" ? "text-gray-300" : "hover:text-gray-300"
//                 }`}
//               >
//                 Buy
//                 <BiChevronDown
//                   className={`text-lg transition-transform duration-200 ${
//                     openMenu === "buy" ? "rotate-180 opacity-60" : ""
//                   }`}
//                 />
//               </button>

//               <div
//                 className={`absolute left-0 mt-2 w-52 bg-white text-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all duration-200 origin-top ${
//                   openMenu === "buy"
//                     ? "scale-100 opacity-100 visible"
//                     : "scale-95 opacity-0 invisible"
//                 }`}
//               >
//                 {hamburgerMenuItem?.[0]?.dropdown.map((item) => (
//                   <div
//                     key={item}
//                     onClick={() => {
//                       window.scrollTo({ top: 0, behavior: "smooth" });
//                       navigate("/properties");
//                     }}
//                     className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition text-sm"
//                   >
//                     {item}
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* SELL DROPDOWN */}
//             <div
//               className="relative"
//               style={{ zIndex: Z_INDEX.dropdown }}
//               onMouseEnter={() => handleMouseEnter("sell")}
//               onMouseLeave={handleMouseLeave}
//             >
//               <button
//                 onClick={() => toggleMenu("sell")}
//                 className={`flex items-center gap-1 text-sm xl:text-base transition ${
//                   openMenu === "sell" ? "text-gray-300" : "hover:text-gray-300"
//                 }`}
//               >
//                 Sell
//                 <BiChevronDown
//                   className={`text-lg transition-transform duration-200 ${
//                     openMenu === "sell" ? "rotate-180 opacity-60" : ""
//                   }`}
//                 />
//               </button>

//               <div
//                 className={`absolute left-0 mt-2 w-56 bg-white text-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all duration-200 origin-top ${
//                   openMenu === "sell"
//                     ? "scale-100 opacity-100 visible"
//                     : "scale-95 opacity-0 invisible"
//                 }`}
//               >
//                 {hamburgerMenuItem?.[1]?.dropdown.map((item) => (
//                   <div
//                     onClick={() => {
//                       window.scrollTo({ top: 0, behavior: "smooth" });
//                       navigate("/admin/properties");
//                     }}
//                     key={item}
//                     className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition text-sm"
//                   >
//                     {item}
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>

//           <button
//             onClick={() => {
//               window.location.href = "/interior";
//               window.scrollTo({ top: 0, behavior: "smooth" });
//             }}
//             className={`hidden lg:flex items-center gap-1 text-sm xl:text-base transition text-gray-300 hover:text-gray-300}`}
//           >
//             Interior
//           </button>

//           {/* Post Property Button - Icon-only on small screens */}
//           <button
//             onClick={handlePostProperty}
//             className="hidden lg:flex items-center justify-center gap-1.5 md:gap-2 bg-btn-brandRed  hover:bg-btn-brandRedHover
//         px-3 md:px-4 lg:px-5 py-1.5 sm:py-2 md:py-2 text-white font-normal text-sm md:text-base shadow-md hover:shadow-lg
//         transform-gpu transition-all duration-100 ease-out rounded-full
//         active:scale-[0.97] active:shadow-sm outline-none focus-visible:ring-2
//         select-none cursor-pointer"
//           >
//             <span className="w-[1.38rem] h-[1.38rem] md:w-5 md:h-5 lg:w-6 lg:h-6 flex flex-shrink-0 select-none items-center justify-center">
//               <img
//                 src={post_property_icon}
//                 alt="post-property"
//                 style={{ zIndex: Z_INDEX.base }}
//                 className="w-full h-full object-contain pointer-events-none select-none transform-gpu"
//                 draggable="false"
//               />
//             </span>
//             <span className="hidden lg:inline transform-gpu">
//               Post Property
//             </span>
//           </button>

//           {/* Icons - Avatar and Menu with Dropdown */}
//           <div className="relative">
//             <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 cursor-pointer">
//               <div
//                 data-user-menu="true"
//                 onClick={() => toggleMenu("user")}
//                 className="hidden sm:flex items-center gap-2"
//               >
//                 <button className="w-8 h-8 mx-1.5 sm:mx-0 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-sm sm:text-base">
//                   {user?.fullName?.[0]?.toUpperCase() || "G"}
//                 </button>
//                 <FiChevronsDown
//                   className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
//                     openMenu === "user" ? "rotate-180" : ""
//                   }`}
//                 />
//               </div>

//               {/* Mobile avatar without dropdown icon */}
//               <button
//                 data-user-menu="true"
//                 onClick={() => toggleMenu("user")}
//                 className="sm:hidden w-8 h-8 mx-1.5 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-bold text-sm"
//               >
//                 {user?.fullName?.[0]?.toUpperCase() || "G"}
//               </button>

//               <div
//                 className="lg:hidden"
//                 onClick={handleSidebarToggle}
//                 data-hamburger="true"
//               >
//                 <HiOutlineMenuAlt3
//                   strokeWidth={2.5}
//                   className="text-white text-2xl sm:text-xl md:text-2xl lg:text-xl cursor-pointer w-8 h-8"
//                   data-hamburger="true"
//                 />
//               </div>
//             </div>

//             {/* User Profile Dropdown */}
//             {openMenu === "user" && (
//               <div
//                 ref={userDropdownRef}
//                 style={{ pointerEvents: "auto", zIndex: Z_INDEX.sidebar }}
//                 className={`absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200
//                   py-3 transform origin-top-right transition-all duration-200 ease-out
//                   ${
//                     openMenu === "user"
//                       ? "scale-100 opacity-100"
//                       : "scale-95 opacity-0"
//                   }`}
//               >
//                 {/* User Info Section */}
//                 <div className="px-5 pb-3 border-b border-gray-200 flex items-center space-x-3">
//                   <button className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-semibold text-base shadow-sm">
//                     {user?.fullName?.[0]?.toUpperCase() || "G"}
//                   </button>
//                   <div className="flex-1 min-w-0">
//                     <p className="text-sm font-semibold text-gray-900 truncate">
//                       {user?.fullName || "Guest User"}
//                     </p>
//                     <p className="text-xs text-gray-500 truncate">
//                       {user?.email || "Not logged in"}
//                     </p>
//                   </div>
//                 </div>

//                 {/* Menu Items */}
//                 <div className="py-1.5">
//                   {sidebarMenuItems.map((item, index) => (
//                     <button
//                       key={index}
//                       onClick={() => handleItemClick(item)}
//                       className="w-full cursor-pointer flex items-center justify-between px-5 py-2.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-200 group"
//                     >
//                       <div className="flex items-center space-x-3">
//                         <item.icon className="h-4.5 w-4.5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
//                         <span className="font-medium text-sm">
//                           {item.label}
//                         </span>
//                       </div>
//                       {item.badge && (
//                         <span className="px-2 py-0.5 bg-indigo-600 text-white text-xs rounded-full font-semibold shadow-sm">
//                           {item.badge}
//                         </span>
//                       )}
//                     </button>
//                   ))}
//                 </div>

//                 {/* Divider */}
//                 <div className="border-t border-gray-200 my-1"></div>

//                 {/* Sign Out / Sign In */}
//                 {user ? (
//                   <button
//                     onClick={handleSignOut}
//                     className="flex items-center space-x-3 px-5 py-2.5 text-gray-700 hover:text-red-600 hover:bg-red-50/80 transition-all duration-200 group w-full"
//                   >
//                     <FiLogOut className="h-4.5 w-4.5 text-gray-500 group-hover:text-red-600 transition-colors" />
//                     <span className="font-medium text-sm">Sign Out</span>
//                   </button>
//                 ) : (
//                   <button
//                     onClick={handleSignIn}
//                     className="flex items-center space-x-3 px-5 py-2.5 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-200 group w-full"
//                   >
//                     <FiUser className="h-4.5 w-4.5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
//                     <span className="font-medium text-sm">Sign In</span>
//                   </button>
//                 )}
//               </div>
//             )}
//           </div>
//         </div>
//       </header>

//       {/* Sidebar Overlay */}
//       {sidebarOpen && (
//         <div
//           className="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
//           style={{ zIndex: Z_INDEX.navbar + 1 }}
//         />
//       )}

//       {/* Sidebar */}
//       <div
//         ref={sidebarRef}
//         className={`fixed top-0 right-0 h-full w-60 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out  lg:hidden ${
//           sidebarOpen ? "translate-x-0" : "translate-x-full"
//         }`}
//         style={{ zIndex: Z_INDEX.navbar + 2 }}
//       >
//         {/* Sidebar Header */}
//         <div className="flex items-center justify-between px-4 md:px-6 lg:px-10 py-1.5 h-16 sm:h-18 md:h-20  border-b border-gray-200">
//           <h2 className="font-semibold text-gray-800">Menu</h2>
//           <button
//             onClick={() => setSidebarOpen(false)}
//             className="p-2 rounded-full hover:bg-gray-100 transition-colors"
//           >
//             <FiX className="w-6 h-6 text-gray-600" />
//           </button>
//         </div>

//         {/* Sidebar Content */}
//         <div className=" flex flex-col h-full p-3 space-y-8">
//           <div className=" space-y-1 overflow-y-auto ">
//             {/* Track which dropdown is open in sidebar */}
//             {hamburgerMenuItem.map((item, index) => {
//               const isDropdownOpen = openMenu === item.title;

//               return (
//                 <div key={index} className="space-y-2">
//                   <button
//                     onClick={() => {
//                       if (item.dropdown) {
//                         setOpenMenu(isDropdownOpen ? null : item.title);
//                       } else {
//                         setSidebarOpen(false);
//                         navigate("/properties");
//                       }
//                     }}
//                     className="w-full flex px-4 items-center justify-between  py-2 text-gray-800 hover:bg-gray-100 rounded-lg transition-colors font-medium"
//                   >
//                     <span>{item.title}</span>
//                     {item.dropdown && (
//                       <BiChevronDown
//                         className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
//                           isDropdownOpen ? "rotate-180" : ""
//                         }`}
//                       />
//                     )}
//                   </button>

//                   {/* Dropdown items */}
//                   {item.dropdown && isDropdownOpen && (
//                     <div className="pl-2">
//                       {item.dropdown.map((subItem, subIndex) => (
//                         <button
//                           key={subIndex}
//                           onClick={() => {
//                             setSidebarOpen(false);
//                             navigate("/properties");
//                           }}
//                           className="w-full text-left px-4 py-2  text-[0.8em]  text-gray-600  hover:bg-gray-50 rounded-lg  transition-colors "
//                         >
//                           {subItem}
//                         </button>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               );
//             })}
//           </div>
//           {/* Post Property Button */}
//           <button
//             onClick={() => {
//               handlePostProperty();
//               setSidebarOpen(false);
//             }}
//             className="
//                 w-full mx-auto flex  items-center justify-center box-border gap-2
//                 px-3 py-3
//                 text-[#0C3861] font-medium
//                 rounded-lg
//                 bg-white
//                 ring-1 ring-[#0C3861]
//                 hover:bg-[rgba(12,56,97,0.08)]
//                 active:bg-[rgba(12,56,97,0.12)]
//                 transition-all duration-200
//                 shadow-sm
//                 hover:shadow-md
//                 "
//           >
//             <span className="w-5 h-5 flex items-center justify-center">
//               <img
//                 src={post_property_blue_icon}
//                 alt="post-property"
//                 className="w-full h-full object-contain"
//               />
//             </span>
//             <span>Post Property</span>
//           </button>
//         </div>
//       </div>
//     </>
//   );
// };

// export default Navbar;
